import { create } from 'zustand'
import paymentApi, { PAYMENT_METHODS } from '../services/paymentApi'

/**
 * Payment Store
 * Zustand store для управления состоянием платежей
 */
const usePaymentStore = create((set, get) => ({
  // Выбранный способ оплаты
  selectedMethod: PAYMENT_METHODS.TELEGRAM_STARS,
  
  // Доступные способы оплаты
  availableMethods: [],
  
  // Пакеты для текущего способа оплаты
  packages: [],
  
  // Состояния загрузки
  isLoadingMethods: false,
  isLoadingPackages: false,
  isProcessingPayment: false,
  
  // ID пакета, который сейчас обрабатывается
  processingPackageId: null,
  
  // Ошибки
  methodsError: null,
  packagesError: null,
  paymentError: null,

  /**
   * Установить выбранный способ оплаты
   * @param {string} method - ID способа оплаты
   */
  setSelectedMethod: (method) => {
    console.log('💳 Выбран способ оплаты:', method)
    set({ selectedMethod: method })
    // Автоматически загружаем пакеты для нового способа
    get().loadPackages(method)
  },

  /**
   * Загрузить доступные способы оплаты
   */
  loadAvailableMethods: async () => {
    set({ isLoadingMethods: true, methodsError: null })
    
    try {
      const methods = await paymentApi.getAvailablePaymentMethods()
      console.log('✅ Загружены доступные способы оплаты:', methods)
      set({ 
        availableMethods: methods,
        isLoadingMethods: false 
      })
    } catch (error) {
      console.error('❌ Ошибка загрузки способов оплаты:', error)
      set({ 
        methodsError: error.message,
        isLoadingMethods: false 
      })
    }
  },

  /**
   * Загрузить пакеты для конкретного способа оплаты
   * @param {string} method - ID способа оплаты
   */
  loadPackages: async (method) => {
    set({ isLoadingPackages: true, packagesError: null })
    
    try {
      const packages = await paymentApi.fetchPackages(method)
      
      // Сортируем пакеты: самый дешевый первый, затем популярные
      const getPackagePrice = (pkg) => {
        // Единый способ получить «цену» для сортировки под разные платежки
        return pkg.stars || pkg.price || pkg.sbp1 || pkg.sbp2 || pkg.crypto1 || pkg.uzs1 || pkg.kzt1 || pkg.card1 || pkg.cardww1 || 0
      }

      const minPrice = Math.min(...packages.map(getPackagePrice))

      const sortedPackages = [...packages].sort((a, b) => {
        const aPrice = getPackagePrice(a)
        const bPrice = getPackagePrice(b)

        if (aPrice === minPrice && bPrice !== minPrice) return -1
        if (bPrice === minPrice && aPrice !== minPrice) return 1

        if (a.popular && !b.popular) return -1
        if (b.popular && !a.popular) return 1

        return aPrice - bPrice
      })
      
      console.log(`✅ Загружены пакеты для ${method}:`, sortedPackages)
      set({ 
        packages: sortedPackages,
        isLoadingPackages: false 
      })
    } catch (error) {
      console.error(`❌ Ошибка загрузки пакетов для ${method}:`, error)
      set({ 
        packagesError: error.message,
        isLoadingPackages: false 
      })
    }
  },

  /**
   * Создать платеж
   * @param {Object} packageData - Данные пакета
   * @param {Function} onSuccess - Callback при успехе
   * @param {Function} onError - Callback при ошибке
   */
  createPayment: async (packageData, onSuccess, onError) => {
    const { selectedMethod } = get()
    
    set({ 
      isProcessingPayment: true, 
      processingPackageId: packageData.id,
      paymentError: null 
    })
    
    try {
      console.log(`💰 Создание платежа через ${selectedMethod}:`, packageData)
      
      // Создаем платеж через API
      const paymentData = await paymentApi.createPayment(selectedMethod, packageData)
      
      // Открываем окно оплаты
      paymentApi.openPayment(selectedMethod, paymentData, async (result) => {
        if (result.success) {
          console.log('✅ Платеж успешно завершен')
          set({ 
            isProcessingPayment: false,
            processingPackageId: null 
          })
          if (onSuccess) await onSuccess(result)
        } else {
          console.error('❌ Платеж не завершен:', result.error)
          set({ 
            isProcessingPayment: false,
            processingPackageId: null,
            paymentError: result.error 
          })
          if (onError) onError(result.error)
        }
      })
      
    } catch (error) {
      console.error('❌ Ошибка создания платежа:', error)
      set({ 
        isProcessingPayment: false,
        processingPackageId: null,
        paymentError: error.message 
      })
      if (onError) onError(error.message)
    }
  },

  /**
   * Сбросить ошибки
   */
  clearErrors: () => {
    set({ 
      methodsError: null,
      packagesError: null,
      paymentError: null 
    })
  },

  /**
   * Сбросить состояние store
   */
  reset: () => {
    set({
      selectedMethod: PAYMENT_METHODS.TELEGRAM_STARS,
      packages: [],
      isLoadingPackages: false,
      isProcessingPayment: false,
      processingPackageId: null,
      packagesError: null,
      paymentError: null
    })
  }
}))

export default usePaymentStore
